/**
 * <s-product-gallery> — Compact product media gallery: one main slide at a time.
 *
 * Navigation: native horizontal swipe/scroll on the track, labeled prev/next buttons,
 * and a thumbnail group with a roving tab stop (Arrow keys, Home, End).
 *
 * Required child markup:
 *   [data-gallery-track]                    — Horizontal scroll-snap track.
 *   [data-gallery-slide][data-media-id]     — One full-width slide per product media.
 *   [data-gallery-thumb][data-media-id]     — Optional thumbnail buttons, same order as slides.
 *   [data-gallery-prev], [data-gallery-next] — Optional step buttons.
 *   [data-gallery-counter]                  — Optional visible "n / total" badge.
 *   [data-gallery-status]                   — Optional polite live region for explicit navigation.
 *   script[data-variant-media]              — JSON map of variant id to associated media id.
 *
 * Element dataset:
 *   data-product-id      — Matches the product form's data-product-id.
 *   data-status-template — Translated status text with an [index] placeholder.
 *
 * External coupling:
 *   s-variant-picker dispatches `product:variant-change` (detail.variantId) on the stable
 *   [data-product-form] host; option `change` events on that host are a fallback.
 *   Variants with no associated media leave the gallery unchanged.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #resizeObserver;
  #index = 0;
  #target = null;
  #targetTimer;
  #slides = [];
  #thumbs = [];
  #variantMedia = {};

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.track = this.querySelector('[data-gallery-track]');
    this.#slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.#thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    if (!this.track || this.#slides.length === 0) return;

    try {
      const map = this.querySelector('script[data-variant-media]');
      this.#variantMedia = map ? JSON.parse(map.textContent || '{}') : {};
    } catch {
      this.#variantMedia = {};
    }

    const initial = this.#slides.findIndex((slide) => !slide.hasAttribute('inert'));
    this.#setActive(initial < 0 ? 0 : initial, { announce: false });
    this.#holdTarget(this.#index);
    this.#scrollToIndex(this.#index, false);

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.goTo(this.#index - 1), opts);
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.goTo(this.#index + 1), opts);

    this.#thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.goTo(i), opts);
    });
    this.querySelector('[data-gallery-thumbs]')?.addEventListener('keydown', this.#onThumbKeydown, opts);

    // Swipes and native scrolling settle on a slide; the most visible slide becomes active.
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          const i = this.#slides.indexOf(entry.target);
          // During a programmatic jump, ignore slides passed on the way to the target.
          if (this.#target !== null && i !== this.#target) continue;
          if (i !== -1 && i !== this.#index) this.#setActive(i, { announce: false });
        }
      }
    }, { root: this.track, threshold: [0.6] });
    this.#slides.forEach((slide) => this.#observer.observe(slide));

    // Slide widths change with the viewport; keep the active slide in place instead of letting snap drift.
    let lastWidth = this.track.clientWidth;
    this.#resizeObserver = new ResizeObserver(() => {
      const width = this.track.clientWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      this.#holdTarget(this.#index);
      this.#scrollToIndex(this.#index, false);
    });
    this.#resizeObserver.observe(this.track);

    const productId = this.dataset.productId;
    const form = productId ? document.querySelector(`[data-product-form][data-product-id="${productId}"]`) : null;
    form?.addEventListener('product:variant-change', (event) => {
      this.#showVariantMedia(event.detail?.variantId);
    }, opts);
    // Fallback: follow the option inputs directly so the gallery never depends on the picker module loading.
    form?.addEventListener('change', (event) => {
      const input = event.target;
      const variantId = input?.dataset?.variantId || input?.selectedOptions?.[0]?.dataset?.variantId;
      if (variantId) this.#showVariantMedia(variantId);
    }, opts);
  }

  #showVariantMedia(variantId) {
    const mediaId = this.#variantMedia[String(variantId)];
    if (!mediaId) return;
    const i = this.#slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (i !== -1 && i !== this.#index) this.goTo(i, { announce: false });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
    clearTimeout(this.#targetTimer);
  }

  goTo(index, { announce = true } = {}) {
    const clamped = Math.max(0, Math.min(this.#slides.length - 1, index));
    this.#holdTarget(clamped);
    this.#setActive(clamped, { announce });
    this.#scrollToIndex(clamped, true);
  }

  #holdTarget(index) {
    this.#target = index;
    clearTimeout(this.#targetTimer);
    this.#targetTimer = setTimeout(() => { this.#target = null; }, 1000);
    this.track.addEventListener('scrollend', () => { this.#target = null; }, { once: true, signal: this.#ac.signal });
  }

  #setActive(index, { announce }) {
    this.#index = index;

    this.#slides.forEach((slide, i) => {
      slide.toggleAttribute('inert', i !== index);
      // Lazy media becomes eager once its slide is shown so swipes don't reveal blank frames.
      if (i === index) slide.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    });

    this.#thumbs.forEach((thumb, i) => {
      const active = i === index;
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
      thumb.tabIndex = active ? 0 : -1;
    });
    this.#scrollThumbIntoView(index);

    const total = this.#slides.length;
    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev) prev.disabled = index === 0;
    if (next) next.disabled = index === total - 1;

    const counter = this.querySelector('[data-gallery-counter]');
    if (counter) counter.textContent = `${index + 1} / ${total}`;

    const status = this.querySelector('[data-gallery-status]');
    if (status && announce && this.dataset.statusTemplate) {
      status.textContent = this.dataset.statusTemplate.replace('[index]', String(index + 1));
    }
  }

  #scrollToIndex(index, smooth) {
    const slide = this.#slides[index];
    if (!slide) return;
    const rtl = getComputedStyle(this.track).direction === 'rtl';
    const left = index * this.track.clientWidth * (rtl ? -1 : 1);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left, behavior: smooth && !reduce ? 'smooth' : 'auto' });
  }

  #scrollThumbIntoView(index) {
    const thumb = this.#thumbs[index];
    const strip = thumb?.parentElement;
    if (!thumb || !strip) return;
    const thumbStart = thumb.offsetLeft - strip.offsetLeft;
    const target = thumbStart - (strip.clientWidth - thumb.offsetWidth) / 2;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Scroll only the strip itself, never the page.
    strip.scrollTo({ left: target, behavior: reduce ? 'auto' : 'smooth' });
  }

  #onThumbKeydown = (event) => {
    const current = this.#thumbs.indexOf(document.activeElement);
    if (current === -1) return;

    const rtl = getComputedStyle(this).direction === 'rtl';
    let next = null;
    if (event.key === 'ArrowRight') next = current + (rtl ? -1 : 1);
    else if (event.key === 'ArrowLeft') next = current + (rtl ? 1 : -1);
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.#thumbs.length - 1;
    if (next === null) return;

    event.preventDefault();
    next = Math.max(0, Math.min(this.#thumbs.length - 1, next));
    this.goTo(next);
    this.#thumbs[next].focus();
  };
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', SProductGallery);
